import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithMutation } from 'jotai-tanstack-query';
import { countGraphemes } from 'unicode-segmenter/grapheme';
import { ValidationError } from 'yup';

import { postUserProfileImageApi } from '@flipster/apis/v1/user';
import { PutUserProfileParams, SocialType } from '@flipster/apis/v1/user/types';
import { FormattedMessage, i18nBunja } from '@flipster/i18n';
import { overlayItemBunja } from '@flipster/overlay';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { createStepComponent } from '@flipster/react/misc/step/Steps';
import { createStepsAtom } from '@flipster/react/misc/step/atom';
import { getDetailedErrorResponse } from '@flipster/utils/axios';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { formatDatetime } from '@flipster/utils/datetime';
import { EnvScope } from '@flipster/utils/env';
import { getErrorCode } from '@flipster/utils/error';
import { deleteLocalStorage, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import { map } from '@flipster/utils/map';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';
import { ProfileEvent } from '@flipster/utils/segment/events/profile';

import { profileSettingBunja } from '../../../shared/community/profile';
import { profileBunja } from '../../bunja';
import { visibilityToBoolean } from '../../misc';
import { oauthProviderSearchParamKey } from './shared/oauth';
import { UsernameValidationMessage, usernameSchema } from './username-schema';

const socialLinkProviders: { label: string; provider: SocialType }[] = [
  {
    label: 'Telegram',
    provider: 'TELEGRAM',
  },
  {
    label: 'YouTube',
    provider: 'YOUTUBE',
  },
  // legacy, only for users who already register their twitter link.
  {
    label: 'Twitter (X)',
    provider: 'X',
  },
];

export const socialLinkDomains: Record<SocialType, string> = {
  X: 'https://x.com/',
  TELEGRAM: 'https://t.me/',
  YOUTUBE: 'https://youtube.com/@',
};

interface ValidationResult {
  valid: boolean;
  message?: string;
}

export type EditProfileModel = {
  form: undefined;
  public: { secondaryLabel: string; onSecondary: () => void };
};

export const editProfileBunja = bunja(() => {
  const env = bunja.use(EnvScope);
  const store = bunja.use(JotaiStoreScope);
  const { profileAtom } = bunja.use(profileBunja);

  // master trader
  const isMasterTraderAtom = atom((get) => get(profileAtom)?.copyTradeMasterProfile != null);

  // profile image (master trader only)
  const baseProfileImageUrlAtom = atom((get) => get(profileAtom)?.copyTradeMasterProfile?.profileIconUrl);

  type ProfileImageDraft = { type: 'change'; blob: Blob; previewUrl: string } | { type: 'remove' } | null;
  const draftProfileImageAtom = atom<ProfileImageDraft>();

  const profileImagePreviewAtom = atom((get) => {
    const draft = get(draftProfileImageAtom);
    if (draft?.type === 'remove') return undefined;
    if (draft?.type === 'change') return draft.previewUrl;
    return get(baseProfileImageUrlAtom);
  });

  const profileImagePristineAtom = atom((get) => get(draftProfileImageAtom) == null);

  // step
  const Steps = createStepComponent<EditProfileModel>();
  const steps = createStepsAtom<EditProfileModel>({ step: 'form', context: undefined });

  // username
  const baseUsernameAtom = atom((get) => get(profileAtom)?.nickname);
  const edittingUsernameAtom = atom<string | undefined>(undefined);
  const usernameAtom = atom(
    (get) => get(edittingUsernameAtom) ?? get(baseUsernameAtom),
    (_, set, value: string) => {
      set(edittingUsernameAtom, value);
      set(usernameServerValidationMessageAtom, undefined);
    },
  );
  const usernamePristineAtom = atom(
    (get) => get(edittingUsernameAtom) == null || get(edittingUsernameAtom) == get(baseUsernameAtom),
  );
  const usernameValidationAtom = atom<ValidationResult>((get) => {
    if (get(usernamePristineAtom)) return { valid: true };

    const message = get(usernameServerValidationMessageAtom);
    if (message) return { valid: false, message };

    const edittingUsername = get(edittingUsernameAtom);
    try {
      usernameSchema.validateSync(edittingUsername);
    } catch (e) {
      if (e instanceof ValidationError) {
        return { valid: false, message: UsernameValidationMessage[e.message] };
      }
      return { valid: false };
    }
    return { valid: true };
  });
  const usernameServerValidationMessageAtom = atom<string | undefined>(undefined);

  // introduction
  const maxIntroductionLength = 150;
  const baseIntroductionAtom = atom((get) => get(profileAtom)?.introduction ?? '');
  const draftIntroductionAtom = atom<string | undefined>(undefined);
  const introductionAtom = atom(
    (get) => get(draftIntroductionAtom) ?? get(baseIntroductionAtom),
    (_, set, value: string) => set(draftIntroductionAtom, value),
  );
  const introductionPristineAtom = atom((get) => {
    const draft = get(draftIntroductionAtom);
    const base = get(baseIntroductionAtom);
    return draft == null || draft === base;
  });
  const introductionLengthAtom = atom((get) => countGraphemes(get(introductionAtom)));
  const introductionValidationAtom = atom((get) => {
    if (get(introductionLengthAtom) > maxIntroductionLength) return { valid: false, type: 'MAX_LENGTH' };
    return { valid: true };
  });

  // social links
  interface SocialLinkFormItem {
    link: string;
    count: string;
  }
  const createSocialLinkFormAtoms = (type: SocialType) => {
    const baseAtom = atom((get): SocialLinkFormItem => {
      const profile = get(profileAtom)?.socialProfiles.find(({ socialType }) => socialType === type);
      return {
        link: map(profile, ({ profileUrl }) => strip(type, profileUrl)) ?? '',
        count: map(profile, ({ numOfFollowers }) => numOfFollowers?.toString()) ?? '',
      };
    });
    const draftAtom = atom<Partial<SocialLinkFormItem>>({ link: undefined, count: undefined });
    const formAtom = atom(
      (get) => ({ link: get(socialLinkWithDomainAtom), count: get(countAtom) }),
      (get, set, field: 'link' | 'count', value: string) => {
        if (field === 'link') set(draftAtom, { ...get(draftAtom), link: strip(type, value) });
        if (field === 'count') set(draftAtom, { ...get(draftAtom), count: value });
      },
    );
    // link
    const socialLinkWithDomainAtom = atom((get) => {
      const domain = socialLinkDomains[type];
      const link = get(draftAtom).link ?? get(baseAtom).link;
      return `${domain}${link}`;
    });
    const strippedSocialLinkAtom = atom((get) => strip(type, get(socialLinkWithDomainAtom)));
    // count
    const countAtom = atom((get) => get(draftAtom).count ?? get(baseAtom).count);
    // validation
    const pristineAtom = atom((get) => {
      const linkPristine = get(baseAtom).link == null || get(baseAtom).link === get(strippedSocialLinkAtom);
      const countPristine = get(baseAtom).count == null || get(baseAtom).count === get(countAtom);
      return { link: linkPristine, count: countPristine };
    });
    const validationAtom = atom((get) => {
      const linkValidation = validateLink(get(socialLinkWithDomainAtom));
      const countValidation = (() => {
        // 1. Count is only needed if user is master trader
        if (!get(isMasterTraderAtom)) return true;
        // 2. Allow empty count on removing link
        if (get(strippedSocialLinkAtom) == '' && get(countAtom) == '') return true;
        // 3. Count should be provided if link is editted
        if ((!get(pristineAtom).link || get(baseAtom).count) && !get(countAtom)) {
          return false;
        }
        return true;
      })();
      return { link: linkValidation, count: countValidation };
    });
    return {
      baseAtom,
      draftAtom,
      socialLinkWithDomainAtom,
      strippedSocialLinkAtom,
      pristineAtom,
      validationAtom,
      formAtom,
    };
  };
  const socialForms = {
    X: createSocialLinkFormAtoms('X'),
    TELEGRAM: createSocialLinkFormAtoms('TELEGRAM'),
    YOUTUBE: createSocialLinkFormAtoms('YOUTUBE'),
  };

  // visibility
  const visibilityAtom = atom((get) => get(profileAtom)?.mySettings);
  const showBothToPublicSectionAtom = atom((get) => {
    const visibility = get(visibilityAtom);
    if (!visibility || get(profileAtom)?.socialProfiles.length == 0) return false;
    return [visibility.tradeHistoryVisibility, visibility.performanceVisibility]
      .map(visibilityToBoolean)
      .some((v) => !v);
  });

  // form status
  const pristineAtom = atom((get) =>
    [
      get(profileImagePristineAtom),
      get(usernamePristineAtom),
      get(introductionPristineAtom),
      ...Object.values(socialForms).flatMap(({ pristineAtom }) => Object.values(get(pristineAtom))),
    ].every(Boolean),
  );
  const validationAtom = atom((get) =>
    [
      get(usernameValidationAtom).valid,
      get(introductionValidationAtom).valid,
      ...Object.values(socialForms).flatMap(({ validationAtom }) => Object.values(get(validationAtom))),
    ].every(Boolean),
  );
  const payloadAtom = atom<PutUserProfileParams>((get) => {
    const payload: PutUserProfileParams = {};
    if (!get(usernamePristineAtom)) payload.nickname = get(edittingUsernameAtom);
    if (!get(introductionPristineAtom)) payload.introduction = get(draftIntroductionAtom);
    for (const [socialType, provider] of Object.entries(socialForms)) {
      if (!Object.values(get(provider.pristineAtom)).every(Boolean)) {
        // provide empty string if user wants to remove social link
        const profileUrl = get(provider.draftAtom) ? get(provider.socialLinkWithDomainAtom) : '';
        const count = get(provider.formAtom).count;
        const isReset = strip(socialType as SocialType, profileUrl) == '';
        (payload.socialProfiles ??= []).push({
          socialType: socialType as SocialType,
          profileUrl: isReset ? '' : profileUrl,
          numOfFollowers: count && !isReset ? Number(count) : undefined,
        });
      }
    }
    // make performance/history visibility public if socialProfiles exists
    if (payload.socialProfiles) {
      payload.settings = { performanceVisibility: 'PUBLIC', tradeHistoryVisibility: 'PUBLIC' };
    }
    return payload;
  });
  const needPublicAgreementAtom = atom((get) => {
    if (!get(payloadAtom).socialProfiles) return false;
    const { performanceVisibility, tradeHistoryVisibility } = get(profileAtom)?.mySettings ?? {};
    return [performanceVisibility, tradeHistoryVisibility].some((visibility) => visibility === 'PRIVATE');
  });

  // x oauth
  const socialConnectionBaseAtom = atom((get) => get(profileAtom)?.socialConnections ?? []);
  const xConnectionAtom = atom((get) =>
    get(socialConnectionBaseAtom).find((connection) => connection.socialName == 'X'),
  );
  const socialLinkProvidersAtom = atom((get) => {
    if (get(xConnectionAtom) || !get(socialForms.X.baseAtom).link) {
      return socialLinkProviders.filter((item) => item.provider != 'X');
    }
    return socialLinkProviders;
  });

  // save-and-load
  const draftKey = `flipster.configs.v1.${env.NEXT_PUBLIC_VERSION}.editProfileDraftForOAuth` as const;
  function saveDraft() {
    if (store.get(pristineAtom)) return;
    const username = store.get(edittingUsernameAtom);
    const socialLinks = Object.fromEntries(
      Object.entries(socialForms).map(([provider, { draftAtom }]) => [provider, store.get(draftAtom)]),
    );
    setLocalStorage(draftKey, { username, socialLinks });
  }
  function loadAndClearDraft() {
    const draft = getLocalStorage(draftKey);
    if (!draft) return;
    try {
      const d = draft as unknown as { username: string; socialLinks: Record<SocialType, SocialLinkFormItem> };
      store.set(edittingUsernameAtom, d.username);
      for (const [provider, { draftAtom }] of Object.entries(socialForms)) {
        store.set(draftAtom, d.socialLinks[provider as SocialType]);
      }
    } finally {
      deleteLocalStorage(draftKey);
    }
  }

  bunja.effect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get(oauthProviderSearchParamKey)) return;
    // restore if there is stored draft
    loadAndClearDraft();
    // remaining oauth step and cleanup will be performed at ./social/bunja.tsx
  });

  const initialSocialConnectionSegmentAtom = atom((get) => {
    return { connected_x: get(socialForms.X.baseAtom).link != '', telegram: get(socialForms.TELEGRAM.baseAtom).link };
  });
  const initialSocialProvidersSegmentAtom = atom((get) => {
    return { x: get(socialForms.X.baseAtom).link, telegram: get(socialForms.TELEGRAM.baseAtom).link };
  });
  const saveProfileSegmentAtom = atom((get) => {
    return {
      username: get(usernameAtom)!,
      telegram: get(socialForms.TELEGRAM.formAtom).link,
      connected_x: get(socialForms.X.formAtom).link != '',
    };
  });

  return {
    // step
    Steps,
    steps,
    // master trader
    isMasterTraderAtom,
    // profile image
    profileImagePreviewAtom,
    draftProfileImageAtom,
    // form
    usernameAtom,
    usernamePristineAtom,
    usernameValidationAtom,
    usernameServerValidationMessageAtom,
    edittingUsernameAtom,
    // introduction
    introductionAtom,
    introductionLengthAtom,
    maxIntroductionLength,
    socialLinkProvidersAtom,
    socialForms,
    showBothToPublicSectionAtom,
    pristineAtom,
    validationAtom,
    payloadAtom,
    needPublicAgreementAtom,
    // oauth
    xConnectionAtom,
    // save-and-load
    saveDraft,
    // segment
    initialSocialConnectionSegmentAtom,
    initialSocialProvidersSegmentAtom,
    saveProfileSegmentAtom,
  };
});
editProfileBunja.debugLabel = 'editProfileBunja';

export const editProfileActionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { close } = bunja.use(overlayItemBunja);
  const { getT } = bunja.use(i18nBunja);
  const {
    steps,
    edittingUsernameAtom,
    usernamePristineAtom,
    needPublicAgreementAtom,
    usernameServerValidationMessageAtom,
    payloadAtom,
    saveProfileSegmentAtom,
    draftProfileImageAtom,
  } = bunja.use(editProfileBunja);
  const { putUserProfileMutationAtom } = bunja.use(profileSettingBunja);

  async function uploadProfileImage(blob: Blob): Promise<string> {
    const contentType = blob.type || 'image/jpeg';
    const fileExtension = contentType.split('/').at(-1) ?? 'jpeg';
    const { data: presigned } = await postUserProfileImageApi({ contentType, fileExtension: `.${fileExtension}` });
    const formData = new FormData();
    const key = presigned.fields['key'];
    Object.entries(presigned.fields).forEach(([k, v]) => formData.append(k, v));
    const fileName = key.split('/').at(-1) ?? `profile.${fileExtension}`;
    formData.append('file', new File([blob], fileName, { type: contentType }));
    const response = await fetch(presigned.uploadUrl, { method: 'POST', body: formData });
    if (!response.ok) throw new Error(`Failed to upload profile image: ${response.statusText}`);
    return key;
  }

  async function resolveProfileImagePayload(): Promise<Pick<PutUserProfileParams, 'uploadedProfileImageKey'>> {
    const draft = store.get(draftProfileImageAtom);
    if (draft?.type === 'remove') return { uploadedProfileImageKey: '' };
    if (draft?.type === 'change') {
      const key = await uploadProfileImage(draft.blob);
      return { uploadedProfileImageKey: key };
    }
    return {};
  }

  function closeAndShowUpdatedToast() {
    snackbar.success({ message: <FormattedMessage id='Common.r251107.Your_profile_has_been_updated' /> });
    close();
  }
  const profileMutationAtom = atomWithMutation((get) => {
    const { mutateAsync } = get(putUserProfileMutationAtom);
    return {
      mutationFn: async (request: PutUserProfileParams) => {
        try {
          const imagePayload = await resolveProfileImagePayload();
          await mutateAsync({ ...request, ...imagePayload });
          closeAndShowUpdatedToast();
        } catch (error) {
          const errorCode = getErrorCode(error);
          switch (errorCode) {
            case 'api.user.profile.invalidNickname':
            case 'api.user.profile.duplicatedNickname':
              store.set(usernameServerValidationMessageAtom, UsernameValidationMessage[errorCode]);
              break;
            case 'api.user.profile.nicknameUpdateBlocked': {
              const detailedError = getDetailedErrorResponse<{ nicknameUpdateAvailableAt: string }>(error);
              if (!detailedError) break;
              segment.track(CommunityEvent.UsernameChangeErrorView);
              store.set(
                usernameServerValidationMessageAtom,
                `Username changes are limited to once every 30 days. Next update: ${formatDatetime(detailedError.errorDetails.nicknameUpdateAvailableAt, { format: 'MMM dd, yyyy, HH:mm zz', isUTC: true })}`,
              );
              break;
            }
            default:
              break;
          }
          store.set(steps.resetStepAtom);
        }
      },
    };
  });
  const isProfileUpdatePendingAtom = atom((get) => get(profileMutationAtom).isPending);
  async function mutateProfile(request: PutUserProfileParams) {
    const { mutateAsync } = store.get(profileMutationAtom);
    return mutateAsync(request);
  }
  async function saveProfile() {
    segment.track(ProfileEvent.EditProfileSaveClick, store.get(saveProfileSegmentAtom));
    const t = getT();
    if (store.get(needPublicAgreementAtom)) {
      return store.set(steps.pushAtom, {
        step: 'public',
        context: store.get(usernamePristineAtom)
          ? {
              secondaryLabel: t('Common.r250401.Close'),
              onSecondary: () => store.set(steps.backAtom),
            }
          : {
              secondaryLabel: t('Autogen_common.r251126.s_Save_username_only'),
              onSecondary: async () => {
                try {
                  await mutateProfile({ nickname: store.get(edittingUsernameAtom) });
                } catch {
                  // Do nothing
                }
              },
            },
      });
    }
    try {
      await mutateProfile(store.get(payloadAtom));
    } catch {
      // Do nothing
    }
  }

  async function changeBothVisibilityToPublic() {
    const { mutateAsync } = store.get(profileMutationAtom);
    try {
      await mutateAsync({ settings: { performanceVisibility: 'PUBLIC', tradeHistoryVisibility: 'PUBLIC' } });
      dialog.open({
        title: {
          label: <FormattedMessage id='Autogen_common.r251126.s_Your_performance_and_trading_history_are_now_public' />,
        },
        content: (
          <FormattedMessage id='Autogen_common.r251126.s_Other_users_can_now_view_your_performance_and_trading_history' />
        ),
        buttons: {
          primary: <Dialog.Button hierarchy='primary' label={<FormattedMessage id='Common.r250401.Done' />} />,
        },
      });
    } catch {
      // Do nothing
    }
  }

  bunja.effect(() => {
    return () => {
      const draft = store.get(draftProfileImageAtom);
      if (draft?.type === 'change') URL.revokeObjectURL(draft.previewUrl);
    };
  });

  return {
    mutateProfile,
    saveProfile,
    changeBothVisibilityToPublic,
    isProfileUpdatePendingAtom,
  };
});
editProfileActionBunja.debugLabel = 'editProfileActionBunja';

function strip(type: SocialType, value: string) {
  const placeholder = socialLinkDomains[type];
  const stripped = value.slice(placeholder.length);
  try {
    // for copy-pasting whole links
    return new URL(stripped).pathname.slice(1);
  } catch {
    return stripped;
  }
}

function validateLink(link: string) {
  try {
    const url = new URL(link);
    // i know this is valid url but spec wants this;
    if (/\/\/+/.test(url.pathname)) return false;
    return Boolean(new URL(link));
  } catch {
    return false;
  }
}
